import { selectAppUpdate } from '@src/selectors/appUpdate';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import AppUpdateToast from './AppUpdateToast';

const SNACKBAR_KEY = 'app-update';

// Applied at once here: nothing in flight to lose. Exact paths — `/login/mfa/**` starts with `/login`.
const AUTO_UPDATE_PATHS = ['/login', '/logout'];

// No toast at all here: it reloads by itself after 20 s (AppUpdateToast), and a reload on the
// enrollment screens mints a new setup key, killing the QR already scanned. It shows on the next route.
const HOLD_UPDATE_PREFIX = '/login/mfa/';

type WaitingVersion = {
  worker: ServiceWorker;
  // Unknown until the worker answers; the toast waits for it, the hand-over does not.
  version?: string;
};

const applyWaitingVersion = (waiting: ServiceWorker) => {
  navigator.serviceWorker.addEventListener(
    'controllerchange',
    () => {
      window.location.reload();
    },
    { once: true }
  );
  waiting.postMessage({ type: 'SKIP_WAITING' });
};

const askVersion = (waiting: ServiceWorker): Promise<string> => {
  waiting.postMessage({ type: 'GET_APP_VERSION' });
  return new Promise(resolve => {
    const abortController = new AbortController();
    const timeout = setTimeout(() => {
      resolve('');
      abortController.abort();
    }, 2000);

    navigator.serviceWorker.addEventListener(
      'message',
      (event: MessageEvent) => {
        if (event.data?.type === 'APP_VERSION') {
          clearTimeout(timeout);
          resolve(event.data.version || '');
          abortController.abort();
        }
      },
      { signal: abortController.signal }
    );
  });
};

const AppUpdate: React.FC = () => {
  const status = useSelector(selectAppUpdate);
  const snackbar = useSnackbar();
  const { pathname } = useLocation();
  const [waiting, setWaiting] = useState<WaitingVersion | null>(null);
  // One hand-over per page load, so a version that fails to take over cannot loop the tab.
  const handedOver = useRef(false);
  const toastShown = useRef(false);
  const closeSnackbar = useRef(snackbar.closeSnackbar);
  closeSnackbar.current = snackbar.closeSnackbar;

  // Once per update found: locate the waiting worker and ask its version. No UI here.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return undefined;

    if (!status.startsWith('updatefound') || !navigator.serviceWorker) {
      setWaiting(null);
      return undefined;
    }

    let stale = false;
    navigator.serviceWorker
      .getRegistration()
      .then(async reg => {
        const worker = reg?.waiting;
        if (!worker || stale) return;
        setWaiting({ worker });
        const version = await askVersion(worker);
        if (!stale) setWaiting({ worker, version });
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Error during service worker update:', error);
      });

    return () => {
      stale = true;
    };
  }, [status]);

  // On every route: decide what the known waiting version does here. Cheap, no messages.
  useEffect(() => {
    const hideToast = () => {
      if (!toastShown.current) return;
      toastShown.current = false;
      snackbar.closeSnackbar(SNACKBAR_KEY);
    };

    if (!waiting || handedOver.current) {
      hideToast();
      return;
    }

    if (AUTO_UPDATE_PATHS.includes(pathname)) {
      handedOver.current = true;
      hideToast();
      applyWaitingVersion(waiting.worker);
      return;
    }

    if (pathname.startsWith(HOLD_UPDATE_PREFIX)) {
      hideToast();
      return;
    }

    if (waiting.version === undefined || toastShown.current) return;

    toastShown.current = true;
    snackbar.enqueueSnackbar('', {
      key: SNACKBAR_KEY,
      persist: true,
      anchorOrigin: { vertical: 'top', horizontal: 'center' },
      content: () => (
        <div>
          <AppUpdateToast
            version={waiting.version ?? ''}
            onUpdate={() => {
              hideToast();
              applyWaitingVersion(waiting.worker);
            }}
          />
        </div>
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting, pathname]);

  useEffect(() => () => closeSnackbar.current(SNACKBAR_KEY), []);

  return null;
};

export default AppUpdate;
